import type React from 'react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { logError } from '@zg-rentals/log-error';
import { type POIType } from '../POIMarkers';
import { DynamicMap } from '../DynamicMap';
import { CommuteAutocomplete, CommuteTimeCards } from '../Commute';
import type { CommuteDestination, CommuteState, TravelMode } from '../utils/commuteTypes';
import { calculateCommuteTimes } from '../utils/routeCalculation';
import { useListingDataOverride, useNeighborhoodContextSafe } from '../context';
import {
  commuteLabel,
  commuteSection,
  mapContainer,
  poiContainer,
  poiPill,
  scrollFadeWrapper,
  spokeBody,
} from './neighborhoodSpoke.styles';

type NormalizedListing = {
  id: string | undefined;
  geo: { lat: number; lon: number } | undefined;
  displayName: string | undefined;
  address: string;
  iconType: string | undefined;
  listingType: string | undefined;
  propertyType: string | undefined;
  unitType: string | undefined;
  buildingType: string;
  userItemTypes: Array<string> | undefined;
  isFavorited: boolean;
  isActive: boolean | undefined;
};

const formatAddress = (addr: { street?: string; city?: string; state?: string } | undefined): string => {
  if (!addr) return '';
  return [addr.street, addr.city, addr.state].filter(Boolean).join(', ');
};

/**
 * Normalize listing data from either the context override (byCoords listing)
 * or Redux currentListingDetails into a common shape for the spoke.
 */
const buildListingData = (
  override: Record<string, unknown> | null,
  reduxListing: Record<string, unknown> | null,
): NormalizedListing | undefined => {
  const src = override || reduxListing;
  if (!src) return undefined;

  const addr = src.address as { street?: string; city?: string; state?: string } | undefined;

  return {
    id: src.maloneLotIdEncoded as string | undefined,
    geo: src.geo as { lat: number; lon: number } | undefined,
    displayName: (src.displayName || src.propertyName) as string | undefined,
    address: formatAddress(addr),
    iconType: src.iconType as string | undefined,
    listingType: src.listingType as string | undefined,
    propertyType: src.propertyType as string | undefined,
    unitType: src.unit as string | undefined,
    buildingType: src.building ? 'building' : 'unit',
    userItemTypes: src.userItemTypes as Array<string> | undefined,
    isFavorited: (src.userItemTypes as Array<string> | undefined)?.includes('favorited') ?? false,
    isActive: src.active as boolean | undefined,
  };
};

type NeighborhoodSpokeContentProps = {
  onCommuteInputFocusChange?: (focused: boolean) => void;
  config?: unknown;
};

/**
 * NeighborhoodSpokeContent — body content for the neighborhood spoke.
 *
 * Renders map, POI pills, and commute calculator. Used by both:
 * - NeighborhoodSpoke (mobile FullscreenOverlay)
 * - DetailPanel (desktop)
 *
 * Reads listing data from ListingDataContext (when provided by FeedCard)
 * or falls back to Redux currentListingDetails (when on HDP).
 */
export const NeighborhoodSpokeContent = ({ onCommuteInputFocusChange }: NeighborhoodSpokeContentProps = {}) => {
  const listingOverride = useListingDataOverride();
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  const listing = buildListingData(listingOverride, currentListing as Record<string, unknown> | null);

  const displayAddress = listing?.address || 'Address not available';

  const contextValue = useNeighborhoodContextSafe();
  const [activePOIs, setActivePOIs] = useState<Set<POIType>>(new Set());
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);

  const state = contextValue?.state;
  const actions = contextValue?.actions;
  const commute = state?.commute;

  const handleDestinationSelect = useCallback(
    async (destination: CommuteDestination) => {
      if (!listing?.geo || !actions) {
        if (actions) {
          actions.setCommuteError('No listing location available');
        }
        return;
      }

      actions.setCommuteDestination(destination);
      actions.setCommuteStatus('calculating');

      try {
        const times = await calculateCommuteTimes({ lat: listing.geo.lat, lng: listing.geo.lon }, destination);
        actions.setCommuteTimes(times);
        if (!commute?.selectedMode && times.length > 0) {
          actions.setSelectedMode(times[0].mode);
        }
      } catch (error) {
        console.error('Failed to calculate commute times:', error);
        actions.setCommuteError('Unable to calculate commute times. Please try again.');
      }
    },
    [listing?.geo, actions, commute?.selectedMode],
  );

  const handleEditCommute = useCallback(() => {
    if (!actions) return;
    setShouldAutoFocus(true);
    actions.clearCommute();
  }, [actions]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  const handleModeSelect = useCallback(
    (mode: TravelMode) => {
      if (!actions) return;
      actions.setSelectedMode(mode);
    },
    [actions],
  );

  const getCommuteState = (): CommuteState => {
    if (!commute) return 'input';
    switch (commute.status) {
      case 'idle':
        return 'input';
      case 'calculating':
        return 'calculating';
      case 'success':
        return 'results';
      case 'error':
        return 'error';
      default:
        return 'input';
    }
  };

  const commuteState = getCommuteState();

  const poiTypes: Array<{ type: POIType; label: string }> = [
    { type: 'restaurants', label: 'Restaurants' },
    { type: 'groceries', label: 'Groceries' },
    { type: 'cafes', label: 'Cafes' },
    { type: 'shopping', label: 'Shopping' },
    { type: 'nightlife', label: 'Nightlife' },
  ];

  const handlePOIClick = (type: POIType) => {
    const newActivePOIs = new Set(activePOIs);
    if (newActivePOIs.has(type)) {
      newActivePOIs.delete(type);
    } else {
      newActivePOIs.add(type);
    }
    setActivePOIs(newActivePOIs);
  };

  if (!contextValue) {
    console.warn('NeighborhoodSpokeContent: No NeighborhoodProvider found.');
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#6e6e6e' }}>
        Neighborhood exploration is not available.
      </div>
    );
  }

  const allowExpandedMapBounds = commuteState === 'results' && !!commute?.destination;

  return (
    <div data-testid="neighborhood-spoke-content">
      {listing?.geo ? (
        <div className={mapContainer} data-testid="neighborhood-map">
          <DynamicMap
            lat={listing.geo.lat}
            lon={listing.geo.lon}
            address={displayAddress}
            height={402}
            zoom={15}
            listing={listing}
            activePOITypes={activePOIs}
            onMapLoad={handleMapLoad}
            allowExpandedBounds={allowExpandedMapBounds}
            commuteDestination={commute?.destination || null}
            selectedCommuteMode={commute?.selectedMode}
            onPOIError={(error, poiType) => {
              logError({
                error,
                errorType: 'POILoadError',
                context: {
                  errorLocation: 'NeighborhoodSpokeContent#onPOIError',
                  errorClass: 'POIMarkers',
                  poiType,
                  listingId: listing?.id,
                  address: displayAddress,
                  coordinates: listing?.geo ? { lat: listing.geo.lat, lng: listing.geo.lon } : null,
                  activePOITypes: Array.from(activePOIs).join(','),
                  googlePlacesStatus: error.message,
                },
              });
            }}
          />
        </div>
      ) : (
        <div className={mapContainer} data-testid="no-coordinates">
          <div className={spokeBody}>
            <div>Map unavailable - No coordinates provided</div>
            <div>Address: {displayAddress}</div>
          </div>
        </div>
      )}

      <div className={scrollFadeWrapper}>
        <div className={poiContainer} data-testid="poi-filters">
          {poiTypes.map(({ type, label }) => (
            <button
              key={type}
              className={poiPill}
              data-active={activePOIs.has(type)}
              onClick={() => handlePOIClick(type)}
              data-testid={`poi-pill-${type}`}
              aria-pressed={activePOIs.has(type)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={commuteSection} data-testid="commute-calculator">
        {commuteState === 'input' && (
          <>
            <div className={commuteLabel}>Calculate your commute times</div>
            <CommuteAutocomplete
              value={commute?.inputValue || ''}
              onChange={actions?.setInputValue || (() => {})}
              onDestinationSelect={handleDestinationSelect}
              onFocusChange={onCommuteInputFocusChange}
              placeholder="Add a destination"
              autoFocus={shouldAutoFocus}
              data-testid="commute-autocomplete"
            />
          </>
        )}

        {(commuteState === 'calculating' || commuteState === 'results' || commuteState === 'error') &&
          commute?.destination && (
            <CommuteTimeCards
              destination={commute.destination}
              commuteTimes={commute.times || []}
              isLoading={commuteState === 'calculating'}
              error={commuteState === 'error' ? commute.error || 'An error occurred' : undefined}
              onEdit={handleEditCommute}
              selectedMode={commute.selectedMode}
              onModeSelect={handleModeSelect}
              data-testid="commute-results"
            />
          )}
      </div>
    </div>
  );
};
